import {
  createMutationStore,
  createQueryStore,
  useQueryClient,
} from '@ferreri/minato-react-utils';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useCategoryImages } from '../category-images/category-images.store';
import { usePublicationAvailableListFromCategoryMap } from '../publications/publications.store';
import { CATEGORIES } from '../routes/constants';
import {
  deleteCategory,
  getCategories,
  getCategory,
  postCategory,
  updateCategory,
} from './categories.client';

export const useCategoryIdQueryParam = () => {
  const router = useRouter();

  return router?.query?.category_id as string | undefined;
};

export const useCategoriesList = () => {
  return createQueryStore({
    queryKey: ['categories'],
    queryFn: getCategories,
  });
};

export const useCurrentCategory = () => {
  const categoryId = useCategoryIdQueryParam();

  return createQueryStore({
    queryKey: ['category', categoryId],
    queryFn: () => getCategory(categoryId as string),
    enabled: Boolean(categoryId),
  });
};

export const useCreateCategory = (config = {}) => {
  return createMutationStore({
    mutationFn: postCategory,
    ...getCombinedConfig(config),
  });
};

export const useUpdateCategory = (config = {}) => {
  return createMutationStore({
    mutationFn: updateCategory,
    ...getCombinedConfig(config),
  });
};

export const useDeleteCategory = (config = {}) => {
  return createMutationStore({
    mutationFn: deleteCategory,
    ...getCombinedConfig(config),
  });
};

export const useCategoriesListCombinedWithImages = () => {
  const { data: categoriesList, isLoading: categoriesIsLoading } =
    useCategoriesList();
  const { data: categoryImages, isLoading: categoryImagesIsLoading } =
    useCategoryImages();

  const isLoading = categoriesIsLoading || categoryImagesIsLoading;

  const categoriesCombinedWithImages = useMemo(() => {
    return categoriesList?.map((category) => {
      return {
        ...category,
        category_image: categoryImages.find(
          ({ category_id }) => category_id === category?.id,
        ),
      };
    });
  }, [categoriesList, categoryImages]);

  return { data: categoriesCombinedWithImages, isLoading };
};

export const useCategoriesListByPublicationCategory = () => {
  const { data: categoriesList, isLoading: isCategoriesListLoading } =
    useCategoriesList();
  const {
    data: publicationListByCategoryMap,
    isLoading: isPublicationListLoading,
  } = usePublicationAvailableListFromCategoryMap();

  const isLoading = isCategoriesListLoading || isPublicationListLoading;

  const categoriesListFromPublicationCategoryResult = useMemo(() => {
    return categoriesList?.filter((category) =>
      publicationListByCategoryMap.has(`${category.id}`),
    );
  }, [categoriesList, publicationListByCategoryMap]);

  return { data: categoriesListFromPublicationCategoryResult, isLoading };
};

export const useCategoriesListByPublicationCategorySliced = () => {
  const { data: categoriesList, isLoading } =
    useCategoriesListByPublicationCategory();

  const categoriesListSlicedResult = useMemo(
    () => categoriesList?.slice(0, 8),
    [categoriesList],
  );

  return { data: categoriesListSlicedResult, isLoading };
};

export const useCategoriesOptions = () => {
  const { data: categoriesList, ...query } = useCategoriesList();

  const options = useMemo(() => {
    return (
      categoriesList?.map((category) => ({
        value: category?.id,
        label: category?.name,
      })) ?? []
    );
  }, [categoriesList]);

  return { data: options, ...query };
};

export const getCombinedConfig = (config) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  return {
    ...config,
    onSuccess: () => {
      router?.push(CATEGORIES);
      queryClient.invalidateQueries();
    },
  };
};
